import React, { useMemo } from 'react';

import { useSelector } from 'react-redux';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';

import { Drawer, Empty } from 'antd';
import { db } from 'firebase/firebase';
import { collection, orderBy,query, where } from 'firebase/firestore';

import { getFullUrl,useFirestoreQuery } from '@src/packages/utils';

import BoxContainer from 'components/BoxContainer';
import Title from 'components/BoxContainer/components/Title';
import FilterContainer from 'components/FilterContainer';
import InfiniteScroll from 'components/InfiniteScroll';

import { PATHS } from 'packages/dashboard/constants';
import TimelineEvent from 'packages/events/components/TimelineEvent';

import IntlMessages from 'util/IntlMessages';

import useFirestoreQueryBatchedPagination from '../../../utils/hooks/useFirestoreQueryBatchedPagination/useFirestoreQueryBatchedPagination';

const TimelineByUser = () => {
  const { userId } = useParams();
  const history = useHistory();
  const currentPageFromState = history.location.state?.currentPage || 1;
  const { url } = useRouteMatch();

  const orgId = useSelector(state => state.organizations.organization.id);
  const { data: userData, loading: userLoading } = useFirestoreQuery(
    query(collection(db, 'organizations', orgId, 'users'), where('id', '==', userId)),
    [orgId, userId],
  );

  const user = userData?.[0];
  const userDivisions = user?.divisions;
  const divisionsPerUser = useMemo(() => {
    if (userLoading || !userDivisions) return [];
    return Object.keys(userDivisions);
  }, [userDivisions, userLoading]);

  const queryByDivId = useMemo(
    () =>
      divisionsPerUser.map(divId =>
        query(
          collection(db, 'organizations', orgId, 'divisions', divId, 'events'),
          where('uid', '==', userId),
          orderBy('createdAtDevice', 'desc'),
        ),
      ),
    [divisionsPerUser, orgId, userId],
  );

  const { data, loading, next, gotNewData } = useFirestoreQueryBatchedPagination(queryByDivId, [
    divisionsPerUser,
  ]);

  const sortedData = useMemo(
    () => [...data].sort((a, b) => (a.createdAtDevice < b.createdAtDevice ? 1 : -1)),
    [data],
  );
  const mainLoading = loading || userLoading;

  return (
    <Drawer open={!!userId} width="100%" closable={false} footer={null}>
      <BoxContainer>
        <BoxContainer content fixed>
          <FilterContainer
            goBack={() =>
              history.push({
                pathname: getFullUrl(PATHS.ACTIVE_USER_DASHBOARD, url),
                state: {
                  currentPage: currentPageFromState,
                },
              })
            }
            title={
              <Title.Header
                value={<IntlMessages id="activeUserDashboard.timeline.drawer.title" />}
              />
            }
          />
        </BoxContainer>
        <BoxContainer content loading={mainLoading}>
          {sortedData.length > 0 ? (
            sortedData.map(evt => <TimelineEvent event={evt} key={evt.id} />)
          ) : (
            <Empty description={<IntlMessages id="general.empty.state" />} />
          )}
          <InfiniteScroll condition={!mainLoading && gotNewData} callback={next} />
        </BoxContainer>
      </BoxContainer>
    </Drawer>
  );
};

export default TimelineByUser;
